/* Shared helpers for replacing a third-party app's product cards with the
   theme's own card.

   Two pages need this, for the same reason. Both are rendered by an app that
   draws its own product card - the Wishlist Hero proxy page
   (/a/wishlist-hero/wishlist) and the Searchanise results page
   (/pages/search-results-page). CSS can bring those cards close, but it cannot
   add what the app has no data for: colour swatches, the size chips revealed
   on hover, the second image on hover, or a struck-through sale price. Those
   come from Liquid. So the real card is fetched instead of imitated.

   Only the genuinely identical parts live here - fetching a card, adopting the
   assets it ships with, and the flash guard. Each page keeps its own
   orchestration, because what they do with the cards differs: the wishlist
   builds a fresh grid and binds a remove control, the search page swaps each
   result in place so Searchanise's own pagination and facets keep working.

   Consumers use window.ThemeCardSwap. */
window.ThemeCardSwap = (function () {
  /* sections/card-product-swap.liquid renders exactly one `card-product-var`
     with the same settings templates/collection.json gives the collection grid
     (portrait ratio, secondary image on, vendor and rating off, quick_add
     standard), so what comes back IS a collection card. */
  var SECTION = 'card-product-swap';

  function basename(url) {
    return url.split('?')[0].split('/').pop();
  }

  /* The fetched section carries the assets its card needs. On these pages
     nothing else loads them - no section renders a product card - so the card
     would otherwise arrive unstyled and inert.

     Scripts matter as much as stylesheets: card-product-var.liquid ships
     `card-interactions.js`, which is what runs the size chips. Without it the
     `<card-interactions>` element never upgrades, so chips do not reveal on
     mobile and clicking one on desktop does not add to cart. A cloned <script>
     node does not execute, so a fresh element is created with the same src.
     Anything already present is skipped by filename, so repeated swaps do not
     stack duplicates. */
  function adoptAssets(doc) {
    var links = doc.querySelectorAll('link[rel="stylesheet"]');
    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href');
      if (!href) continue;
      if (document.querySelector('link[href*="' + basename(href) + '"]')) continue;
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    }

    var scripts = doc.querySelectorAll('script[src]');
    for (var j = 0; j < scripts.length; j++) {
      var src = scripts[j].getAttribute('src');
      if (!src) continue;
      if (document.querySelector('script[src*="' + basename(src) + '"]')) continue;
      var script = document.createElement('script');
      script.src = src;
      script.defer = true;
      document.head.appendChild(script);
    }
  }

  /* Resolves to a detached `.card-wrapper` element, or rejects. Callers decide
     what a failure means - both current callers treat it as "leave the app's
     own cards alone" rather than showing a partial grid. */
  function fetchCard(handle, variant) {
    var url = '/products/' + handle + '?section_id=' + SECTION +
      (variant ? '&variant=' + encodeURIComponent(variant) : '');
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error(res.status + ' for ' + handle);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        adoptAssets(doc);
        var card = doc.querySelector('.card-wrapper') || doc.querySelector('.card');
        if (!card) throw new Error('no card markup for ' + handle);
        return card;
      });
  }

  /* Pulls a product handle (and variant, when present) out of a link. Both
     apps expose the product only as an href on their own card. */
  function readProductLink(link) {
    if (!link) return null;
    var url;
    try {
      url = new URL(link.getAttribute('href'), window.location.origin);
    } catch (e) {
      return null;
    }
    var match = url.pathname.match(/\/products\/([^/]+)/);
    if (!match) return null;
    return { handle: match[1], variant: url.searchParams.get('variant') };
  }

  /* Flash guard. Both apps paint their own cards as soon as their bundle
     boots, and the swap cannot start until those cards exist - they are where
     the product identity is read from. Without this the shopper watches one
     card style get replaced by another.

     A class goes on <html> at parse time, before the app can paint, and CSS
     hides the app's cards while it is set. It is a promise to reveal
     SOMETHING, so every path that ends without theme cards must call reveal().
     claim() restarts the failsafe, which matters on a slow load: a single
     fixed timer would fire mid-fetch and show the very cards this hides. */
  function createFlashGuard(className, timeoutMs) {
    var timer = null;

    function reveal() {
      window.clearTimeout(timer);
      document.documentElement.classList.remove(className);
    }

    function claim() {
      document.documentElement.classList.add(className);
      window.clearTimeout(timer);
      timer = window.setTimeout(reveal, timeoutMs || 8000);
    }

    function held() {
      return document.documentElement.classList.contains(className);
    }

    return { claim: claim, reveal: reveal, held: held };
  }

  /* The theme card ships the same `.wishlist-hero-custom-button` Wishlist Hero
     binds on every normal template, with its data-wlh-* attributes already on
     it. The app does not bind injected nodes - it only scans on its own pass -
     so the button is asked to bind itself, and without this the card renders
     with an empty space where every other grid on the store shows a heart.

     Two quirks, both established by probing the live SDK rather than from
     docs: it resolves its target with querySelector('.' + ButtonClassElement),
     i.e. the FIRST match, so each button needs its own throwaway class; and
     `WishlistHash` is required by its validator while GetWishListHash()
     answers 403 "available for paid plans only" on this shop - an empty string
     satisfies it and the button still initialises.

     Returns the heart element when it was asked to bind, so callers that need
     to watch it (the wishlist reconciles removals) can. */
  function bindWishlistHeart(cardNode, token) {
    var sdk = window.WishListHero_SDK;
    var heart = cardNode.querySelector('.wishlist-hero-custom-button');
    if (!heart) return null;
    if (heart.children.length > 0) return heart;
    if (!sdk || typeof sdk.InitializeAddToWishListButton !== 'function') return heart;

    heart.classList.add(token);
    try {
      sdk.InitializeAddToWishListButton({
        ButtonClassElement: token,
        ProductId: Number(heart.getAttribute('data-wlh-id')),
        ProductVariantId: Number(heart.getAttribute('data-wlh-variantid')),
        ProductTitle: heart.getAttribute('data-wlh-name'),
        ProductLink: heart.getAttribute('data-wlh-link'),
        ProductImage: heart.getAttribute('data-wlh-image'),
        ProductPrice: Number(heart.getAttribute('data-wlh-price')),
        ButtonMode: heart.getAttribute('data-wlh-mode') || 'icon_only',
        WishlistHash: '',
      });
    } catch (e) {
      /* Leaves the button unpainted; nothing else depends on it. */
    }
    return heart;
  }

  /* Rebuilds are driven off <body>, not off the app's own container: these
     apps render late and replace their whole subtree, which would strip an
     observer attached to any node inside it. Debounced, and callers key their
     work so a rebuild only happens when the product list actually changed. */
  function observeDocument(callback, delay) {
    var timer = null;
    new MutationObserver(function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(callback, delay || 250);
    }).observe(document.body, { childList: true, subtree: true });
  }

  return {
    fetchCard: fetchCard,
    adoptAssets: adoptAssets,
    readProductLink: readProductLink,
    bindWishlistHeart: bindWishlistHeart,
    createFlashGuard: createFlashGuard,
    observeDocument: observeDocument,
  };
})();
